import { useEffect, useRef, useState, type RefObject } from 'react';

type UseGridColumnsResult = [RefObject<HTMLDivElement>, number | null];

const getGridColumnCount = (element: HTMLDivElement): number | null => {
  const computed = window.getComputedStyle(element);
  const template = computed.gridTemplateColumns?.trim();
  if (!template || template === 'none') return null;
  if (template === '1fr') return 1;

  const minmaxCount = (template.match(/minmax\(/g) || []).length;
  if (minmaxCount) return minmaxCount;

  const tokens = template
    .split(' ')
    .map(token => token.trim())
    .filter(token => token && token !== '/' && token !== 'none');

  return tokens.length || null;
};

export const useGridColumns = (): UseGridColumnsResult => {
  const ref = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState<number | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const update = () => {
      if (!ref.current) return;
      const nextCount = getGridColumnCount(ref.current);
      if (!nextCount) return;
      setColumns(prev => (prev === nextCount ? prev : nextCount));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, columns];
};
